import React, { FC, useEffect } from "react";

import {
  DOWNLOAD_MANAGER_FORMAT_BY,
  downloadManagerPreferenceFormatByState,
  tabLoadingAtom,
  useShareToken,
} from "@keepeek/commons";
import {
  DownloadManagerActions,
  DownloadManagerActionsList,
  DownloadManagerFormatSelector,
  DownloadManagerHeader,
  DownloadManagerModal,
  DownloadManagerTypeLists,
  FormatActions,
} from "@keepeek/refront-components";
import { Box } from "@mui/material";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { configSectionComponentDownloadManagerSelector } from "../../providers/config/selectors";
import { askDownloadStepState } from "../../providers/downloadManager/atoms/askDownloads";
import { DownloadUrlForEmailStatus } from "../../providers/downloadManager/atoms/emailSend";
import { downloadManagerSelectedFormatsByTypeState } from "../../providers/downloadManager/atoms/formats";
import { useDownloadManager } from "../../providers/downloadManager/hooks";
import { useAttachmentsDownloadManager } from "../../providers/downloadManager/hooks/attachments";
import AskDownload from "./AskDownload";
import DownloadManagerAttachmentsElementLists from "./DownloadManagerAttachmentsElementLists";
import DownloadManagerAttachmentsUnitarySharedElement from "./DownloadManagerAttachmentsUnitarySharedElement";
import DownloadManagerElementLists from "./DownloadManagerElementLists";

const DownloadManager: FC = function ({}) {
  const { pathname, query } = useRouter();
  const { isShare, shareTokenAttachementAuthorized, isUnitaryShareElement } = useShareToken(
    pathname,
    query.tokenId,
  );
  const { t } = useTranslation("");

  const configDownloadManager = useRecoilValue(configSectionComponentDownloadManagerSelector);
  const setDownloadManagerSelectedFormatsByTypeState = useSetRecoilState(
    downloadManagerSelectedFormatsByTypeState,
  );
  const { formatBy: enableFormatByElement, enableFormatFavorite } = configDownloadManager ?? {
    formatBy: undefined,
    enableFormatFavorite: undefined,
  };
  // Hide other formats in public share if definition is not HD. We do not have access to attachmentsVisible flag :
  // if definition is HD with attachmentsVisible to false, tab will be visible with no attachments listed.
  const enableOtherFormats =
    configDownloadManager?.enableOtherFormats &&
    (!isShare || (isShare && shareTokenAttachementAuthorized));

  const [preferenceFormat, setPreferenceFormat] = useRecoilState(
    downloadManagerPreferenceFormatByState,
  );
  const {
    open,
    dataviewKey,
    closeDownloadManager,
    selection,
    elementsToDownloadCount,
    elementsDownloadRequestCount,
    emptyFormatsByTypes,
    emptyFormatsByElements,
    formatsByTypes,
    formatsByElements,
    loadingByTypes,
    loadingByElements,
    selectFormatByType,
    formatFavorites,
    selectFormatByElement,
    download,
    clickDownloadLoading,
    askDownloadStep,
    conditions,
    downloadUrlForEmail,
    setDownloadUrlForEmail,
  } = useDownloadManager();

  const { enqueueSnackbar } = useSnackbar();
  const canElementList = selection.length > 1 && enableFormatByElement;

  useEffect(() => {
    if (open && !loadingByTypes && emptyFormatsByTypes) {
      if (canElementList && !emptyFormatsByElements && !loadingByElements) {
        setPreferenceFormat(DOWNLOAD_MANAGER_FORMAT_BY.ELEMENT);
      } else {
        setPreferenceFormat(DOWNLOAD_MANAGER_FORMAT_BY.TYPE);
      }
    }
  }, [
    canElementList,
    emptyFormatsByElements,
    emptyFormatsByTypes,
    enqueueSnackbar,
    loadingByElements,
    loadingByTypes,
    open,
    selection.length,
    setPreferenceFormat,
  ]);

  const isOther = enableOtherFormats && preferenceFormat === DOWNLOAD_MANAGER_FORMAT_BY.OTHER;
  const isTypeLists =
    !isOther && (selection.length === 1 || preferenceFormat === DOWNLOAD_MANAGER_FORMAT_BY.TYPE);

  const tabLoading = useRecoilValue(tabLoadingAtom);

  const onCloseDownloadManager = () => {
    // Reset to first tab while closing download manager
    setPreferenceFormat(DOWNLOAD_MANAGER_FORMAT_BY.TYPE);
    setDownloadManagerSelectedFormatsByTypeState([]);
    setDownloadUrlForEmail({ url: "", status: DownloadUrlForEmailStatus.NOT_LOADED });
    closeDownloadManager();
  };

  const handleEmailRedirect = () => {
    const subject = t("download-manager.download-email.title.label");
    const body = t("download-manager.download-email.body.label", {
      downloadUrl: downloadUrlForEmail.url,
    });
    window.open(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      "mail",
    );
  };

  const currentTabLoading = loadingByElements || loadingByTypes || tabLoading; // Todo : only one boolean (tabLoading atom) to represent current tab loading. Only one tab on loading at time

  let actionsAvailable: DownloadManagerActionsList[] = [DownloadManagerActionsList.Download];

  if (elementsToDownloadCount > 0 && elementsDownloadRequestCount > 0) {
    actionsAvailable = [DownloadManagerActionsList.DownloadAndRequest];
  } else if (askDownloadStep || elementsDownloadRequestCount > 0) {
    actionsAvailable = [DownloadManagerActionsList.Request];
  }

  if (conditions.cgu?.enable || conditions.csu?.enable) {
    actionsAvailable = [...actionsAvailable, DownloadManagerActionsList.Conditions];
  }

  if (configDownloadManager?.actions?.mailToButton) {
    actionsAvailable = [...actionsAvailable, DownloadManagerActionsList.MailTo];
  }

  if (
    configDownloadManager?.actions?.mailToButton &&
    downloadUrlForEmail.status === DownloadUrlForEmailStatus.LOADED
  ) {
    actionsAvailable = [...actionsAvailable, DownloadManagerActionsList.MailToRedirect];
  }

  const setAskDownloadStep = useSetRecoilState(askDownloadStepState);

  const { attachmentsByElements } = useAttachmentsDownloadManager();
  const displayOtherTab = attachmentsByElements.find((element) => element.attachments.length !== 0);

  const tabsAvailable =
    !loadingByTypes && !askDownloadStep
      ? [
          DOWNLOAD_MANAGER_FORMAT_BY.TYPE,
          ...(canElementList ? [DOWNLOAD_MANAGER_FORMAT_BY.ELEMENT] : []),
          ...(enableOtherFormats && displayOtherTab ? [DOWNLOAD_MANAGER_FORMAT_BY.OTHER] : []),
        ]
      : [];

  // Do not display download manager if we don't know the context
  if (dataviewKey === undefined) {
    return null;
  }

  const formatBy = () => {
    // Force the first type selector when there's only one selection
    // So we keep the preference for when there's at least two selection
    if (isTypeLists) {
      return (
        <DownloadManagerTypeLists
          onSelectFormat={selectFormatByType}
          formatsByTypes={formatsByTypes}
          loading={loadingByTypes}
          formatsActionsAvailable={
            enableFormatFavorite ? [FormatActions.Favorite, FormatActions.Select] : undefined
          }
          onSelectFormatFavorite={formatFavorites.selectFavorite}
          KIcon={KIcon}
        />
      );
    } else if (isOther) {
      if (isUnitaryShareElement) {
        return <DownloadManagerAttachmentsUnitarySharedElement dataViewKey={dataviewKey} />;
      }
      return <DownloadManagerAttachmentsElementLists dataViewKey={dataviewKey} />;
    } else {
      return (
        <DownloadManagerElementLists
          loading={loadingByElements}
          formatsByElements={formatsByElements}
          onSelectFormat={selectFormatByElement}
          dataviewkey={dataviewKey}
        />
      );
    }
  };

  return (
    <DownloadManagerModal
      loading={currentTabLoading}
      open={open}
      onClose={onCloseDownloadManager}
      scrollbarVisible
      header={
        <>
          <DownloadManagerHeader selection={selection} onClose={onCloseDownloadManager} />
          {/* We don't want to see the format selector when there's only one selection */}
          {/* Because we only remove it from the DOM, it will not trigger a preference change */}
          {/* And we will keep the preference for when there's at least two selection */}
          {(enableOtherFormats || canElementList) && (
            <DownloadManagerFormatSelector
              tab={preferenceFormat}
              onChange={(tab) => {
                setPreferenceFormat(tab.valueOf());
                setAskDownloadStep(false);
              }}
              tabsAvailable={tabsAvailable}
            />
          )}
        </>
      }
      footer={
        !isOther && (
          <DownloadManagerActions
            isLoading={currentTabLoading}
            loading={clickDownloadLoading}
            emailLoading={downloadUrlForEmail.status === DownloadUrlForEmailStatus.LOADING}
            downloadAction={{
              onDownload: () => download(false),
              onSendByEmail: () => {
                setDownloadUrlForEmail({ url: "", status: DownloadUrlForEmailStatus.LOADING });
                download(true);
              },
              onEmailRedirect: () => {
                handleEmailRedirect();
                onCloseDownloadManager();
              },
              askDownloadStep,
            }}
            conditions={conditions}
            count={{
              index: selection.length,
              elementsToDownloadCount,
              elementsDownloadRequestCount,
            }}
            actionsAvailable={actionsAvailable}
          />
        )
      }
    >
      {currentTabLoading ? <Box height={300} /> : askDownloadStep ? <AskDownload /> : formatBy()}
    </DownloadManagerModal>
  );
};

export default DownloadManager;
